import { createBrowserHistory } from 'history';
import { Route, Routes } from 'react-router-dom';
import React, { Suspense, useEffect, useMemo, lazy } from 'react';
import { unstable_HistoryRouter as Router } from 'react-router-dom';

// Constants
import { ROUTES } from './constants/routes-constants';

// Styles
import './styles/main.scss';

// --------------------
// AUTH
// --------------------
import LoginPage from './pages/auth/login';
import RegisterPage from './pages/auth/register';
import ResetPassword from './pages/auth/resetPassword';
import ForgetPassword from './pages/auth/forgetPassword';

// --------------------
// ERROR PAGES
// --------------------
import Error500Page from './pages/status/Pagina500';
import NotFoundPage from './pages/status/Pagina404';
import NotAllowedPage from './pages/status/Pagina403';

// Public pages
import PublicGuiaDownload from './pages/public/guias/PublicGuiaDownload';

// --------------------
// REGISTRATION PAGES
// --------------------
const SelectRolePage = lazy(() => import('./pages/auth/select-role'));
const MinerRegistrationPage = lazy(() => import('./pages/auth/miner-registration'));
const PadronDeMinasRegistrationPage = lazy(
  () => import('./pages/auth/padron-de-minas-registration')
);

// --------------------
// USER HOME PAGES
// --------------------
const MinerHomePage = lazy(() => import('./pages/minero/home'));
const NoMinerHomePage = lazy(() => import('./pages/noMinero/home'));
const PadronDeMinasHomePage = lazy(() => import('./pages/padronDeMinas/home'));
const ProfilePage = lazy(() => import('./pages/profile/pages/myInformation'));

// --------------------
// GUIAS
// --------------------
const GuiasEmitidasPage = lazy(() => import('./pages/user/guias'));
const EmisionGuia = lazy(() => import('./pages/guias/EmisionGuia'));

// --------------------
// RESUMENES
// --------------------
const ResumenPage = lazy(() => import('./pages/user/resumen'));

// --------------------
// MODULOS
// --------------------
const ModulosPage = lazy(() => import('./pages/user/modulos'));
const NuevoModuloPage = lazy(() => import('./pages/user/modulos/nuevo'));

// --------------------
// ADMIN PAGES
// --------------------
const AltaEmpresaPage = lazy(() => import('./pages/admins/productores/alta'));
const EmpresasAdminPage = lazy(() => import('./pages/admins/productores/list'));
const DetalleEmpresaAdminPage = lazy(() => import('./pages/admins/productores/details'));

const AltaNoProductorPage = lazy(() => import('./pages/admins/no-productores/alta'));
const NoProductoresAdminPage = lazy(() => import('./pages/admins/no-productores/list'));
const DetalleNoProductorAdminPage = lazy(() => import('./pages/admins/no-productores/details'));

const EstablecimientosMinerosAdminPage = lazy(() => import('./pages/admins/establecimientos/list'));
const DetalleEstablecimientoMineroAdminPage = lazy(
  () => import('./pages/admins/establecimientos/details/minero')
);
const AltaEstablecimientoMineroPage = lazy(
  () => import('./pages/admins/establecimientos/alta/minero')
);
const EstablecimientoNoProductorAlta = lazy(
  () => import('./pages/admins/establecimientos/alta/noMinero')
);
const EstablecimientoNoProductorDetails = lazy(
  () => import('./pages/admins/establecimientos/details/noMinero')
);

const UserEstablecimientosPage = lazy(() => import('./pages/user/establecimientos/list'));
const NuevoEstablecimiento = lazy(() => import('./pages/noMinero/establecimientos/list/nuevo'));
const UserEstablecimientosDetailsPage = lazy(() => import('./pages/user/establecimientos/details'));

const UserCateosPage = lazy(() => import('./pages/user/cateos/list'));
const UserBuquesDetailsPage = lazy(() => import('./pages/user/buques/details'));

// --------------------
// ADMIN - Padrones de Mineras
// --------------------
const AltaPadronDeMinasPage = lazy(() => import('./pages/admins/padronesDeMinas/alta'));
const PadronesDeMinasAdminPage = lazy(() => import('./pages/admins/padronesDeMinas/list'));
const PadronDeMinasDetailsPage = lazy(() => import('./pages/admins/padronesDeMinas/details'));

const AltaConsecionMineraPage = lazy(() => import('./pages/admins/concesionesMineras/alta'));
const EditarConsecionMineraPage = lazy(() => import('./pages/admins/concesionesMineras/editar'));

// --------------------
// ADMIN - Cateos
// --------------------
const CateosAdminPage = lazy(() => import('./pages/admins/cateos/list'));
const CateoAltaPage = lazy(() => import('./pages/admins/cateos/alta'));
const CateoDetailsPage = lazy(() => import('./pages/admins/cateos/details'));

const BuquesAdminPage = lazy(() => import('./pages/admins/buques/list'));
const BuqueAltaPage = lazy(() => import('./pages/admins/buques/alta'));
const BuqueDetailsPage = lazy(() => import('./pages/admins/buques/details'));

// --------------------
// ADMIN - Guías
// --------------------
const GuiasAdminPage = lazy(() => import('./pages/admins/guias/list'));
import GuiaDetailsPage from './pages/admins/guias/details';

// --------------------
// ADMIN - Comprobantes
// --------------------
const ComprobantesAdminPage = lazy(() => import('./pages/admins/comprobantes/list'));

// Regular imports for utilities and components
import { useAppSelector } from './store/store';
import { PageLoading } from '@ant-design/pro-layout';
import { ProtectedRoute } from './components/routes';

// Services
import MeService from './services/me.service';

// Interfaces
import type { IUser } from './interfaces/user';

/** Rutas a las que se puede acceder sin necesidad de autenticarse. */
const publicRoutes = [
  { path: ROUTES.PUBLIC_GUIAS_DOWNLOAD_ROUTE, element: <PublicGuiaDownload /> },
  { path: '*', element: <NotFoundPage /> },
  { path: '/500', element: <Error500Page /> },
  { path: '/403', element: <NotAllowedPage /> }
];

const authRoutes = [
  { path: ROUTES.LOGIN_ROUTE, element: <LoginPage /> },
  { path: ROUTES.REGISTER_ROUTE, element: <RegisterPage /> },
  { path: ROUTES.RESET_PASSWORD_ROUTE, element: <ResetPassword /> },
  { path: ROUTES.FORGOT_PASSWORD_ROUTE, element: <ForgetPassword /> },
  { path: ROUTES.VALIDATE_EMAIL_ROUTE, element: <LoginPage />, hideLayout: true }
];

const isAdmin = (user: IUser) => !!user?.roles.find((r) => r.descripcion.includes('ADMIN'));

const hasTitular = (user: IUser) =>
  user.no_productores.length > 0 ||
  user.productores_mineros.length > 0 ||
  user.padron_mineros.length > 0;

/** Rutas que requieren autenticación. */
const privateRoutes = [
  /* ---------------------------------------
            RUTAS DEL REGISTRO
 ---------------------------------------- */
  { path: ROUTES.SELECT_ROLE_ROUTE, element: <SelectRolePage /> },
  {
    path: ROUTES.MINER_REGISTRATION_ROUTE,
    element: <MinerRegistrationPage />,
    isAllowed: (user: IUser) => user.productores_mineros.length === 0
  },
  // {
  //   path: ROUTES.NON_MINER_REGISTRATION_ROUTE,
  //   element: <NonMinerRegistrationPage />,
  //   isAllowed: (user: IUser) => user.no_productores.length === 0
  // },
  {
    path: ROUTES.PADRON_DE_MINAS_REGISTRATION_ROUTE,
    element: <PadronDeMinasRegistrationPage />,
    isAllowed: (user: IUser) => user.padron_mineros.length === 0
  },

  /* ---------------------------------------
            PERFIL
 ---------------------------------------- */
  { path: ROUTES.PROFILE_ROUTE, element: <ProfilePage />, isAllowed: (user: IUser) => !!user },

  /* ---------------------------------------
            GUÍAS
 ---------------------------------------- */
  {
    path: ROUTES.GUIA_EMISION_ROUTE,
    element: <EmisionGuia />,
    isAllowed: hasTitular
  },
  {
    path: ROUTES.USUARIO_GUIAS_EMITIDAS_ROUTE,
    element: <GuiasEmitidasPage />,
    isAllowed: hasTitular
  },

  {
    path: ROUTES.USUARIO_GUIAS_EMITIDAS_DETAILS_ROUTE,
    element: <GuiaDetailsPage isSelf={true} />,
    isAllowed: hasTitular
  },

  /* ---------------------------------------
            ESTABLECIMIENTOS
 ---------------------------------------- */
  {
    path: ROUTES.USUARIO_ESTABLECIMIENTOS_ROUTE,
    element: <UserEstablecimientosPage />,
    isAllowed: hasTitular
  },
  {
    element: <NuevoEstablecimiento />,
    path: ROUTES.USUARIO_ESTABLECIMIENTOS_NUEVO_ROUTE,
    isAllowed: (user: IUser) => user.no_productores.length > 0
  },
  {
    path: ROUTES.USUARIO_ESTABLECIMIENTOS_DETAILS_ROUTE,
    element: <UserEstablecimientosDetailsPage />,
    isAllowed: hasTitular
  },
  {
    path: ROUTES.USUARIO_BUQUES_DETAILS_ROUTE,
    element: <UserBuquesDetailsPage />,
    isAllowed: hasTitular
  },

  /* ---------------------------------------
            CATEOS
 ---------------------------------------- */
  {
    path: ROUTES.USUARIO_CATEOS_ROUTE,
    element: <UserCateosPage />,
    isAllowed: (user: IUser) => user.productores_mineros.length > 0
  },
  {
    path: ROUTES.USUARIO_CATEOS_DETAILS_ROUTE,
    element: <CateoDetailsPage isAdmin={false} />,
    isAllowed: (user: IUser) => user.productores_mineros.length > 0
  },

  /* ---------------------------------------
            RESUMENES
 ---------------------------------------- */
  {
    path: ROUTES.USUARIO_RESUMEN_ROUTE,
    element: <ResumenPage />,
    isAllowed: (user: IUser) =>
      user.no_productores.length > 0 || user.productores_mineros.length > 0
  },

  /* ---------------------------------------
            MODULOS
 ---------------------------------------- */
  {
    path: ROUTES.USUARIO_MODULOS_ROUTE,
    element: <ModulosPage />,
    isAllowed: (user: IUser) =>
      user.no_productores.length > 0 || user.productores_mineros.length > 0
  },
  {
    path: ROUTES.USUARIO_MODULOS_NUEVO_ROUTE,
    element: <NuevoModuloPage />,
    isAllowed: (user: IUser) =>
      user.no_productores.length > 0 || user.productores_mineros.length > 0
  },

  /* ---------------------------------------
            ADMIN ROUTES
 ---------------------------------------- */

  // Productores Mineros
  { path: ROUTES.ADMIN_PRODUCTORES_ROUTE, element: <EmpresasAdminPage />, isAllowed: isAdmin },
  {
    path: `${ROUTES.ADMIN_PRODUCTORES_ROUTE}/:id`,
    element: <DetalleEmpresaAdminPage />,
    isAllowed: isAdmin
  },
  {
    path: `${ROUTES.ADMIN_PRODUCTORES_ROUTE}/:id/editar`,
    element: <AltaEmpresaPage />,
    isAllowed: isAdmin
  },
  { path: ROUTES.ADMIN_PRODUCTORES_ALTA_ROUTE, element: <AltaEmpresaPage />, isAllowed: isAdmin },

  // No Productores
  {
    path: ROUTES.ADMIN_NO_PRODUCTORES_ROUTE,
    element: <NoProductoresAdminPage />,
    isAllowed: isAdmin
  },
  {
    path: `${ROUTES.ADMIN_NO_PRODUCTORES_ROUTE}/:id`,
    element: <DetalleNoProductorAdminPage />,
    isAllowed: isAdmin
  },
  {
    path: `${ROUTES.ADMIN_NO_PRODUCTORES_ROUTE}/:id/editar`,
    element: <AltaNoProductorPage />,
    isAllowed: isAdmin
  },
  {
    path: ROUTES.ADMIN_NO_PRODUCTORES_ALTA_ROUTE,
    element: <AltaNoProductorPage />,
    isAllowed: isAdmin
  },

  // Establecimientos Mineros
  {
    path: ROUTES.ADMIN_ESTABLECIMIENTOS_MINEROS_ROUTE,
    element: <EstablecimientosMinerosAdminPage />,
    isAllowed: isAdmin
  },
  {
    path: ROUTES.ADMIN_ESTABLECIMIENTOS_MINEROS_DETAILS_ROUTE,
    element: <DetalleEstablecimientoMineroAdminPage />,
    isAllowed: isAdmin
  },
  {
    path: ROUTES.ADMIN_ESTABLECIMIENTOS_MINEROS_ALTA_ROUTE,
    element: <AltaEstablecimientoMineroPage />,
    isAllowed: isAdmin
  },

  // Establecimientos No Mineros
  {
    path: ROUTES.ADMIN_ESTABLECIMIENTOS_NO_MINEROS_ALTA_ROUTE,
    element: <EstablecimientoNoProductorAlta />,
    isAllowed: isAdmin
  },
  {
    path: ROUTES.ADMIN_ESTABLECIMIENTOS_NO_MINEROS_DETAILS_ROUTE,
    element: <EstablecimientoNoProductorDetails />,
    isAllowed: isAdmin
  },

  // Padrones de Mineras
  {
    path: ROUTES.ADMIN_PADRONES_DE_MINAS_ROUTE,
    element: <PadronesDeMinasAdminPage />,
    isAllowed: isAdmin
  },
  {
    path: ROUTES.ADMIN_PADRONES_DE_MINAS_DETAILS_ROUTE,
    element: <PadronDeMinasDetailsPage />,
    isAllowed: isAdmin
  },
  {
    path: ROUTES.ADMIN_PADRONES_DE_MINAS_ALTA_ROUTE,
    element: <AltaPadronDeMinasPage />,
    isAllowed: isAdmin
  },
  {
    path: ROUTES.ADMIN_CONCESIONES_MINERAS_ALTA_ROUTE,
    element: <AltaConsecionMineraPage />,
    isAllowed: isAdmin
  },
  {
    path: ROUTES.ADMIN_CONCESIONES_MINERAS_EDIT_ROUTE,
    element: <EditarConsecionMineraPage />,
    isAllowed: isAdmin
  },

  // Cateos
  {
    path: ROUTES.ADMIN_CATEOS_ROUTE,
    element: <CateosAdminPage />,
    isAllowed: isAdmin
  },
  {
    path: ROUTES.ADMIN_CATEOS_DETAILS_ROUTE,
    element: <CateoDetailsPage isAdmin={true} />,
    isAllowed: isAdmin
  },
  {
    path: ROUTES.ADMIN_CATEOS_ALTA_ROUTE,
    element: <CateoAltaPage />,
    isAllowed: isAdmin
  },

  // Buques
  {
    path: ROUTES.ADMIN_BUQUES_ROUTE,
    element: <BuquesAdminPage />,
    isAllowed: isAdmin
  },
  {
    path: ROUTES.ADMIN_BUQUES_ALTA_ROUTE,
    element: <BuqueAltaPage />,
    isAllowed: isAdmin
  },
  {
    path: ROUTES.ADMIN_BUQUES_DETAILS_ROUTE,
    element: <BuqueDetailsPage />,
    isAllowed: isAdmin
  },

  // Guías
  {
    path: ROUTES.ADMIN_GUIAS_ROUTE,
    element: <GuiasAdminPage />,
    isAllowed: isAdmin
  },
  {
    path: ROUTES.ADMIN_GUIAS_DETAILS_ROUTE,
    element: <GuiaDetailsPage />,
    isAllowed: isAdmin
  },

  // Comprobantes
  {
    path: ROUTES.ADMIN_COMPROBANTES_ROUTE,
    element: <ComprobantesAdminPage />,
    isAllowed: isAdmin
  }
];

export const history = createBrowserHistory();

const RootComponent: React.FC = () => {
  const user = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      console.log('accessToken');
      MeService.getCurrentUser().then();
    }
  }, []);

  const HomePage = useMemo(() => {
    if (!user) return LoginPage;
    if (isAdmin(user)) {
      return EmpresasAdminPage;
    }

    if (user.productores_mineros.length > 0) {
      return MinerHomePage;
    }

    if (user.no_productores.length > 0) {
      return NoMinerHomePage;
    }

    if (user.padron_mineros.length > 0) {
      return PadronDeMinasHomePage;
    }

    return SelectRolePage;
  }, [user]);

  return (
    // @ts-ignore
    <Router history={history}>
      <Routes>
        <Route
          path={ROUTES.HOMEPAGE_ROUTE}
          element={
            user ? (
              <ProtectedRoute user={user} isAllowed={Boolean(user)}>
                <Suspense fallback={<PageLoading />}>{<HomePage />}</Suspense>
              </ProtectedRoute>
            ) : (
              <LoginPage />
            )
          }
        />

        {publicRoutes.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}

        {!user &&
          authRoutes.map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}

        {privateRoutes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={
              <ProtectedRoute
                user={user}
                isAllowed={route.isAllowed ? !!user && route.isAllowed(user) : Boolean(user)}
              >
                <Suspense fallback={<PageLoading />}>{route.element}</Suspense>
              </ProtectedRoute>
            }
          />
        ))}
      </Routes>
    </Router>
  );
};

export default RootComponent;
